import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowDownIcon, ArrowUpIcon, CheckIcon, ChevronRightIcon, Clock3Icon, XIcon } from 'lucide-react';
export type Look = {
  id: number;
  name: string;
  model: string;
  image: string;
  status: 'approved' | 'review' | 'alterations';
  note: string;
};
type RunwayOrderProps = {
  looks: Look[];
  onReorder: (id: number, direction: -1 | 1) => void;
};
const statusStyles: Record<Look['status'], string> = {
  approved: 'bg-black text-[#f4efe4]',
  review: 'border border-black bg-[#f4efe4] text-black',
  alterations: 'bg-[#c51f2d] text-white'
};
export function RunwayOrder({
  looks,
  onReorder
}: RunwayOrderProps) {
  const [selected, setSelected] = useState<Look | null>(null);
  return <section id="runway" className="border-t border-black pt-4" aria-labelledby="runway-title" data-id="element-79">
      <div className="mb-8 grid gap-5 md:grid-cols-[1fr_auto] md:items-end" data-id="element-80">
        <div data-id="element-81">
          <p className="utility-label mb-2" data-id="element-82">01 / RUN OF SHOW</p>
          <h2 id="runway-title" className="editorial-title text-[clamp(3.8rem,9vw,8.8rem)]" data-id="element-83">
            RUNWAY
          </h2>
        </div>
        <div className="max-w-xs border-l border-[#c51f2d] pl-4 text-sm leading-relaxed" data-id="element-84">
          <p className="handwritten text-2xl text-[#c51f2d]" data-id="element-85">
            Hold the silence after look 06.
          </p>
          <p className="mt-1 font-medium" data-id="element-86">24 looks · 8m 40s · Doors 19:30</p>
        </div>
      </div>

      <div className="-mx-4 flex snap-x gap-px overflow-x-auto bg-black px-4 pb-px sm:mx-0 sm:px-0" data-id="element-87">
        {looks.map((look, index) => <motion.article layout key={look.id} className="group relative min-w-[240px] snap-start bg-[#f4efe4] sm:min-w-[270px]" data-id="element-88">
            <button type="button" className="focus-ring block w-full text-left" onClick={() => setSelected(look)} aria-label={`Open look ${index + 1}, ${look.name}`} data-id="element-89">
              <div className="relative aspect-[3/4] overflow-hidden bg-[#d8d1c4]" data-id="element-90">
                <img src={look.image} alt={`${look.name} worn by ${look.model}`} className="h-full w-full object-cover grayscale transition duration-500 group-hover:scale-[1.02] group-hover:grayscale-0" data-id="element-91" />
                <span className="absolute left-3 top-2 font-serif text-6xl text-white drop-shadow-sm" data-id="element-92">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span className={`absolute bottom-3 left-3 px-2 py-1 text-[10px] font-bold uppercase tracking-[.15em] ${statusStyles[look.status]}`} data-id="element-93">
                  {look.status}
                </span>
              </div>
              <div className="flex items-start justify-between p-3" data-id="element-94">
                <div data-id="element-95">
                  <h3 className="font-serif text-xl italic" data-id="element-96">{look.name}</h3>
                  <p className="mt-1 text-[10px] font-bold uppercase tracking-[.18em]" data-id="element-97">
                    {look.model}
                  </p>
                </div>
                <ChevronRightIcon className="mt-1 h-4 w-4" aria-hidden="true" data-id="element-98" />
              </div>
            </button>
            <div className="absolute right-2 top-2 flex gap-1" data-id="element-99">
              <button type="button" onClick={() => onReorder(look.id, -1)} disabled={index === 0} className="focus-ring grid h-8 w-8 place-items-center bg-[#f4efe4] disabled:opacity-30" aria-label={`Move ${look.name} earlier`} data-id="element-100">
                <ArrowUpIcon className="h-4 w-4" data-id="element-101" />
              </button>
              <button type="button" onClick={() => onReorder(look.id, 1)} disabled={index === looks.length - 1} className="focus-ring grid h-8 w-8 place-items-center bg-[#f4efe4] disabled:opacity-30" aria-label={`Move ${look.name} later`} data-id="element-102">
                <ArrowDownIcon className="h-4 w-4" data-id="element-103" />
              </button>
            </div>
          </motion.article>)}
      </div>

      <AnimatePresence data-id="element-104">
        {selected && <motion.div className="fixed inset-0 z-50 flex justify-end bg-black/50" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} role="dialog" aria-modal="true" aria-label={`${selected.name} details`} onClick={() => setSelected(null)} data-id="element-105">
            <motion.div initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'spring',
          damping: 28,
          stiffness: 260
        }} className="h-full w-full max-w-md overflow-y-auto bg-[#f4efe4] p-5 sm:p-8" onClick={event => event.stopPropagation()} data-id="element-106">
              <button type="button" className="focus-ring ml-auto grid h-10 w-10 place-items-center border border-black" onClick={() => setSelected(null)} aria-label="Close look details" data-id="element-107">
                <XIcon className="h-5 w-5" data-id="element-108" />
              </button>
              <img src={selected.image} alt="" className="mt-6 aspect-[4/5] w-full object-cover grayscale" data-id="element-109" />
              <p className="utility-label mt-6" data-id="element-110">
                LOOK{' '}
                {String(looks.findIndex(item => item.id === selected.id) + 1).padStart(2, '0')}
              </p>
              <h3 className="mt-2 font-serif text-5xl italic" data-id="element-111">
                {selected.name}
              </h3>
              <p className="mt-2 text-sm uppercase tracking-[.16em]" data-id="element-112">
                {selected.model}
              </p>
              <div className="mt-8 border-y border-black py-5" data-id="element-113">
                <p className="handwritten text-3xl text-[#c51f2d]" data-id="element-114">
                  {selected.note}
                </p>
              </div>
              <div className="mt-6 flex items-center gap-3 text-sm" data-id="element-115">
                {selected.status === 'approved' ? <CheckIcon className="h-4 w-4" data-id="element-116" /> : <Clock3Icon className="h-4 w-4" data-id="element-117" />}
                Atelier status:{' '}
                <strong className="uppercase" data-id="element-118">{selected.status}</strong>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}